import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  InputAdornment,
  Button,
  DialogActions,
  DialogContentText,
  Typography,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { AddFournisseurModal } from "./FournisseurModals";
import { usePermissions } from "./hooks/usePermissions";
import { ReactSession } from "react-client-session";
import axios from "axios";
import config from "./global/config";

const FournisseurSelectionDialog = ({
  open,
  onClose,
  onSelect,
  fournisseurs = [],
  onFournisseurAdded,
}) => {
  const { hasPermission } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newFournisseurData, setNewFournisseurData] = useState({
    categorie_code: "",
    raison_sociale: "",
    numero_fiscal: "",
    adresse: "",
    telephone: "",
    email: "",
    site_web: "",
  });

  const api = axios.create({
    baseURL: config.apiUrl,
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  const DialogHeader = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant="h5" component="div">
        Sélectionner un Fournisseur
      </Typography>
      {hasPermission("fournisseur", "add") && (
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddModalOpen(true)}
          sx={{ ml: 2 }}
        >
          Nouveau Fournisseur
        </Button>
      )}
    </Box>
  );

  const fetchCategories = async () => {
    try {
      const response = await api.get("selection.php", {
        params: { categorie: "" },
      });
      const categoriesData = Array.isArray(response.data) ? response.data : [];
      setCategories(categoriesData);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddFournisseur = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("categorie_code", newFournisseurData.categorie_code);
      formData.append("raison_sociale", newFournisseurData.raison_sociale);
      formData.append("numero_fiscal", newFournisseurData.numero_fiscal);
      formData.append("adresse", newFournisseurData.adresse);
      formData.append("telephone", newFournisseurData.telephone);
      formData.append("email", newFournisseurData.email);
      formData.append("site_web", newFournisseurData.site_web);
      formData.append("description", newFournisseurData.description);
      formData.append("profession", newFournisseurData.profession);
      formData.append("user_cre", ReactSession.get("username"));
      formData.append("METHOD", "POST");

      const response = await api.post("fournisseur.php", formData);

      if (
        typeof response.data === "string" &&
        response.data.includes("Error")
      ) {
        throw new Error(response.data);
      }

      if (
        Array.isArray(response.data) ||
        (typeof response.data === "object" && response.data.id_fournisseur)
      ) {
        setAddModalOpen(false);
        // Reset form data
        setNewFournisseurData({
          categorie_code: "",
          raison_sociale: "",
          numero_fiscal: "",
          adresse: "",
          telephone: "",
          email: "",
          site_web: "",
          description: "",
          profession: "",
        });

        // Notify parent component to refresh fournisseur list
        if (onFournisseurAdded) {
          onFournisseurAdded();
        }

        // If the response contains the new fournisseur data, select it
        if (typeof response.data === "object" && response.data.id_fournisseur) {
          onSelect(response.data);
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }
      alert(error.message || "Error adding fournisseur. Please try again.");
      console.error(error);
    }
  };

  const handleNewFournisseurChange = (e) => {
    const { name, value } = e.target;
    setNewFournisseurData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Rest of your existing code...
  const filteredFournisseurs = fournisseurs.filter((fournisseur) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (fournisseur?.raison_sociale || "").toLowerCase().includes(searchLower) ||
      (fournisseur?.numero_fiscal || "").toLowerCase().includes(searchLower) ||
      (fournisseur?.site_web || "").toLowerCase().includes(searchLower) ||
      (fournisseur?.telephone || "").includes(searchTerm) ||
      (fournisseur?.email || "").toLowerCase().includes(searchLower) ||
      (fournisseur?.Code_Fournisseur || "").toLowerCase().includes(searchLower)
    );
  });

  const handleRowClick = (fournisseur) => {
    if (fournisseur.description === "N'est pas Confiance") {
      setSelectedFournisseur(fournisseur);
      setConfirmDialogOpen(true);
    } else {
      onSelect(fournisseur);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedFournisseur) {
      onSelect(selectedFournisseur);
    }
    setConfirmDialogOpen(false);
  };

  const handleCancelSelection = () => {
    setConfirmDialogOpen(false);
    setSelectedFournisseur(null);
  };

  const columns = [
    {
      field: "Code_Fournisseur",
      headerName: "Code Fournisseur",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ fontWeight: "medium", color: "primary.main" }}>
          {params.value || ""}
        </Box>
      ),
    },
    {
      field: "raison_sociale",
      headerName: "Raison Sociale",
      width: 130,
      renderCell: (params) => (
        <Box>{`${params.row?.raison_sociale || ""} ${params.row?.numero_fiscal || ""}`}</Box>
      ),
    },
    {
      field: "telephone",
      headerName: "Téléphone",
      width: 130,
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
    },
    {
      field: "site_web",
      headerName: "Site Web",
      width: 130,
    },
    {
      field: "adresse",
      headerName: "Adresse",
      width: 200,
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <DialogHeader />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              placeholder="Rechercher par raison_sociale, code, téléphone..."
              fullWidth
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ height: 400, width: "100%" }}>
            {filteredFournisseurs.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Typography variant="subtitle1" color="text.secondary">
                  Aucun fournisseur trouvé
                </Typography>
                {hasPermission("fournisseur", "add") && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAddModalOpen(true)}
                  >
                    Ajouter un nouveau fournisseur
                  </Button>
                )}
              </Box>
            ) : (
              <DataGrid
                rows={filteredFournisseurs}
                columns={columns}
                getRowId={(row) => row.id_fournisseur}
                onRowClick={(params) => handleRowClick(params.row)}
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 5 } },
                }}
                getRowClassName={(params) => {
                  if (params.row.description === "N'est pas Confiance") {
                    return "warning-row";
                  }
                  return "";
                }}
                sx={{
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  },
                  "& .warning-row": {
                    backgroundColor: "#ffebee",
                    "&:hover": {
                      backgroundColor: "#ffcdd2",
                    },
                  },
                }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSelection}
        maxWidth="sm"
        PaperProps={{
          sx: {
            border: "2px solid #d32f2f",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#d32f2f",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon />
          <Typography variant="h6" component="div">
            ATTENTION - Fournisseur Non Fiable
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                bgcolor: "#fef2f2",
                p: 2,
                borderRadius: 1,
              }}
            >
              <ErrorOutlineIcon color="error" />
              <DialogContentText sx={{ color: "error.main", m: 0 }}>
                Ce fournisseur est marqué comme{" "}
                <strong>"N'est pas Confiance"</strong>
              </DialogContentText>
            </Box>
            <DialogContentText>
              Êtes-vous sûr de vouloir procéder à la sélection de ce fournisseur ?
            </DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#fafafa" }}>
          <Button
            onClick={handleCancelSelection}
            variant="outlined"
            color="error"
            size="large"
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirmSelection}
            variant="contained"
            color="error"
            size="large"
          >
            Confirmer la Sélection
          </Button>
        </DialogActions>
      </Dialog>

      <AddFournisseurModal
        open={addModalOpen}
        handleClose={() => setAddModalOpen(false)}
        handleSubmit={handleAddFournisseur}
        fournisseurData={newFournisseurData}
        handleChange={handleNewFournisseurChange}
        categories={categories}
      />
    </>
  );
};

export default FournisseurSelectionDialog;
